import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ggosDeviceService, enumService, tenantService } from '../services/api';
import {
  Monitor, Wifi, WifiOff, Search, RefreshCw, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ShieldCheck, Package, MapPin, Key, Building2,
  ArrowUpDown, Smartphone, X, Battery, HardDrive,
  Signal, AlertTriangle, Lock, Clock, Activity, User, Car, CircleUser,
} from 'lucide-react';
import './GGOSDeviceList.css';

const AUTO_REFRESH_MS = 30000;

/* ── tiny helpers ── */
const fmtRel = (d) => {
  if (!d) return 'Never';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'Now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h}h`;
  return `${Math.floor(ms / 86400000)}d`;
};

const fmtMB = (mb) => {
  if (mb == null) return '—';
  return mb >= 1000 ? `${(mb / 1024).toFixed(1)}G` : `${mb}M`;
};

const signalLabel = (dbm) => {
  if (dbm == null) return null;
  if (dbm >= -50) return { text: 'Excellent', color: 'green' };
  if (dbm >= -60) return { text: 'Good', color: 'green' };
  if (dbm >= -70) return { text: 'Fair', color: 'yellow' };
  return { text: 'Weak', color: 'red' };
};

const batteryColor = (pct) => {
  if (pct == null) return 'gray';
  if (pct >= 50) return 'green';
  if (pct >= 20) return 'yellow';
  return 'red';
};

// Guard against garbage snapshot readings (e.g. INT_MIN -2147483648).
// A valid battery percent is a finite number in [0, 100].
const validPct = (pct) =>
  (typeof pct === 'number' && Number.isFinite(pct) && pct >= 0 && pct <= 100) ? pct : null;

const Badge = ({ color, children }) => (
  <span className={`dl-badge dl-badge--${color}`}>{children}</span>
);

const ConnDot = ({ status }) => (
  <span className={`dl-dot dl-dot--${status}`} title={status} />
);

/* ── tiny bar (battery/storage) ── */
const MiniBar = ({ pct, color }) => (
  <span className="dl-minibar">
    <span className={`dl-minibar-fill dl-minibar-fill--${color}`} style={{ width: `${Math.min(100, Math.max(0, pct || 0))}%` }} />
  </span>
);

/* ── stat card ── */
const StatCard = ({ icon: Icon, label, value, color, active, onClick }) => (
  <button
    className={`dl-stat ${active ? 'dl-stat--active' : ''}`}
    onClick={onClick}
    title={`Filter: ${label}`}
  >
    <Icon size={18} className={`dl-stat-icon dl-stat-icon--${color}`} />
    <span className="dl-stat-val">{value}</span>
    <span className="dl-stat-lbl">{label}</span>
  </button>
);

/* ── sortable header ── */
const SortTh = ({ field, label, sorting, onSort, align }) => {
  const active = sorting.sortBy === field;
  return (
    <th
      className={`dl-th ${active ? 'dl-th--active' : ''} ${align === 'right' ? 'dl-th--right' : ''}`}
      onClick={() => onSort(field)}
    >
      <span className="dl-th-inner">
        {label}
        {active
          ? (sorting.sortOrder === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
          : <ArrowUpDown size={12} className="dl-th-sort-idle" />}
      </span>
    </th>
  );
};

/* ════════════════════════════════════════════════════════
   Main component
   ════════════════════════════════════════════════════════ */
const GGOSDeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    search: '',
    connectionStatus: '',
    licenseStatus: '',
    inventoryMatch: '',
    tenantId: '',
  });
  const [sorting, setSorting] = useState({ sortBy: 'registered_at', sortOrder: 'desc' });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [enums, setEnums] = useState({});
  const [tenants, setTenants] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  // Fetch enums
  useEffect(() => {
    (async () => {
      try {
        const r = await enumService.getAll();
        setEnums(r.enums || {});
      } catch { /* silent */ }
    })();
  }, []);

  // Fetch tenants for the tenant filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await tenantService.getAll({ pageSize: 100 });
        setTenants(r.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  // Main fetch
  const fetchDevices = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const r = await ggosDeviceService.getAll({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        connectionStatus: filters.connectionStatus,
        licenseStatus: filters.licenseStatus,
        inventoryMatch: filters.inventoryMatch,
        tenantId: filters.tenantId,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      });
      if (isMounted.current) {
        setDevices(r.data || []);
        if (r.pagination) setPagination(p => ({ ...p, ...r.pagination }));
      }
    } catch {
      if (!silent) setError('Failed to load GGOS devices');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters, sorting]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { if (isMounted.current) fetchDevices(true); }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchDevices]);

  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  useEffect(() => {
    if (message || error) { const t = setTimeout(() => { setMessage(null); setError(null); }, 5000); return () => clearTimeout(t); }
  }, [message, error]);

  // Handlers
  const handleSearch = (e) => { setFilters(f => ({ ...f, search: e.target.value })); setPagination(p => ({ ...p, page: 1 })); };
  const handleFilter = (field, value) => { setFilters(f => ({ ...f, [field]: value })); setPagination(p => ({ ...p, page: 1 })); };
  const handleSort = (field) => setSorting(s => ({ sortBy: field, sortOrder: s.sortBy === field && s.sortOrder === 'asc' ? 'desc' : 'asc' }));
  const handlePage = (p) => setPagination(prev => ({ ...prev, page: p }));

  // Fleet stats (computed from ALL fetched devices on current page)
  const stats = useMemo(() => {
    const total = pagination.totalCount;
    const online = devices.filter(d => d.connectionStatus === 'online').length;
    const offline = devices.filter(d => d.connectionStatus === 'offline').length;
    const licensed = devices.filter(d => d.license).length;
    const matched = devices.filter(d => d.inventory?.matched).length;
    const alerting = devices.filter(d => d.alerts?.length > 0).length;
    return { total, online, offline, licensed, matched, alerting };
  }, [devices, pagination.totalCount]);

  // Active filter chips
  const activeFilters = [];
  if (filters.connectionStatus) activeFilters.push({ key: 'connectionStatus', label: `Status: ${filters.connectionStatus}` });
  if (filters.licenseStatus) activeFilters.push({ key: 'licenseStatus', label: `License: ${filters.licenseStatus}` });
  if (filters.inventoryMatch) activeFilters.push({ key: 'inventoryMatch', label: `Inventory: ${filters.inventoryMatch}` });
  if (filters.tenantId) {
    const tName = tenants.find(t => t.id === filters.tenantId)?.name || filters.tenantId;
    activeFilters.push({ key: 'tenantId', label: `Tenant: ${tName}` });
  }

  return (
    <div className="dl-page">
      {/* ── Header ── */}
      <div className="dl-header">
        <div className="dl-header-left">
          <Monitor size={24} className="dl-header-icon" />
          <div>
            <h1 className="dl-title">GGOS Devices</h1>
            <p className="dl-subtitle">{stats.total} registered device{stats.total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="dl-header-right">
          <label className="dl-auto-toggle" title="Auto-refresh every 30s">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            <RefreshCw size={14} className={autoRefresh ? 'dl-spin' : ''} />
            Live
          </label>
        </div>
      </div>

      {message && <div className="alert alert-success dl-alert">{message}</div>}
      {error && <div className="alert alert-error dl-alert">{error}</div>}

      {/* ── Stat strip ── */}
      <div className="dl-stats">
        <StatCard icon={Monitor} label="Total" value={stats.total} color="blue"
          active={!filters.connectionStatus} onClick={() => handleFilter('connectionStatus', '')} />
        <StatCard icon={Wifi} label="Online" value={stats.online} color="green"
          active={filters.connectionStatus === 'online'} onClick={() => handleFilter('connectionStatus', 'online')} />
        <StatCard icon={WifiOff} label="Offline" value={stats.offline} color="red"
          active={filters.connectionStatus === 'offline'} onClick={() => handleFilter('connectionStatus', 'offline')} />
        <StatCard icon={Key} label="Licensed" value={stats.licensed} color="purple"
          active={filters.licenseStatus === 'licensed'} onClick={() => handleFilter('licenseStatus', filters.licenseStatus === 'licensed' ? '' : 'licensed')} />
        <StatCard icon={Package} label="Matched" value={stats.matched} color="teal"
          active={filters.inventoryMatch === 'matched'} onClick={() => handleFilter('inventoryMatch', filters.inventoryMatch === 'matched' ? '' : 'matched')} />
        <StatCard icon={AlertTriangle} label="Alerts" value={stats.alerting} color="orange"
          active={false} onClick={() => {}} />
      </div>

      {/* ── Search + filters ── */}
      <div className="dl-toolbar">
        <div className="dl-search-wrap">
          <Search size={16} className="dl-search-icon" />
          <input
            className="dl-search"
            type="text"
            placeholder="Search thing name, IMEI, serial, Widevine, tenant…"
            value={filters.search}
            onChange={handleSearch}
          />
          {filters.search && (
            <button className="dl-search-clear" onClick={() => handleFilter('search', '')}>
              <X size={14} />
            </button>
          )}
        </div>

        <div className="dl-filters">
          <select className="dl-select" value={filters.tenantId}
            onChange={e => handleFilter('tenantId', e.target.value)}>
            <option value="">All Tenants</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="dl-select" value={filters.connectionStatus}
            onChange={e => handleFilter('connectionStatus', e.target.value)}>
            <option value="">All Status</option>
            {enums.connection_status?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="dl-select" value={filters.licenseStatus}
            onChange={e => handleFilter('licenseStatus', e.target.value)}>
            <option value="">All License</option>
            {enums.license_status_filter?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select className="dl-select" value={filters.inventoryMatch}
            onChange={e => handleFilter('inventoryMatch', e.target.value)}>
            <option value="">All Inventory</option>
            {enums.inventory_match_status?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="dl-chips">
          {activeFilters.map(f => (
            <button key={f.key} className="dl-chip" onClick={() => handleFilter(f.key, '')}>
              {f.label} <X size={12} />
            </button>
          ))}
          <button className="dl-chip dl-chip--clear" onClick={() => setFilters({ search: filters.search, connectionStatus: '', licenseStatus: '', inventoryMatch: '', tenantId: '' })}>
            Clear all
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div className="dl-loading"><div className="dd-spinner" /> Loading…</div>
      ) : devices.length === 0 ? (
        <div className="dl-empty">
          <Monitor size={40} />
          <p>No devices found</p>
          {filters.search && <p className="dl-empty-hint">Try adjusting search or filters</p>}
        </div>
      ) : (
        <div className="dl-table-wrap">
          <table className="dl-table">
            <thead>
              <tr>
                <th className="dl-th dl-th--status" />
                <SortTh field="thing_name" label="Device" sorting={sorting} onSort={handleSort} />
                <th className="dl-th">Tenant / City</th>
                <th className="dl-th">Driver / Vehicle</th>
                <SortTh field="battery_percent" label="Battery" sorting={sorting} onSort={handleSort} />
                <th className="dl-th">Storage / Signal</th>
                <th className="dl-th">Uptime 24h</th>
                <th className="dl-th">License / Inv</th>
                <th className="dl-th">Alerts</th>
                <th className="dl-th">Last Passenger</th>
                <SortTh field="last_connected_at" label="Last Seen" sorting={sorting} onSort={handleSort} align="right" />
              </tr>
            </thead>
            <tbody>
              {devices.map(d => {
                const inv = d.inventory || {};
                const snap = d.snapshot;
                const conn = d.connection24h || {};
                const sig = signalLabel(snap?.signalDbm);
                const storagePct = snap?.storageTotal ? Math.round((snap.storageFree / snap.storageTotal) * 100) : null;
                return (
                  <tr key={d.id} className={`dl-row ${d.alerts?.length ? 'dl-row--alert' : ''}`}>
                    {/* Status dot */}
                    <td className="dl-td dl-td--status">
                      <ConnDot status={d.connectionStatus} />
                    </td>

                    {/* Device name + model/os + hardware */}
                    <td className="dl-td">
                      <Link to={`/ggos-devices/${d.thingName}`} className="dl-device-link">
                        {d.thingName}
                      </Link>
                      <span className="dl-sub">
                        <Smartphone size={11} /> {d.deviceInfo?.model || '—'} · v{d.deviceInfo?.osVersion || '?'}
                      </span>
                      <span className="dl-hw">
                        {d.hardwareIds?.serial || d.hardwareIds?.imei?.substring(0, 10) || '—'}
                      </span>
                    </td>

                    {/* Tenant / City */}
                    <td className="dl-td">
                      {inv.tenantName ? (
                        <span className="dl-tenant">
                          <Building2 size={13} />
                          {inv.tenantId ? (
                            <Link to={`/tenants/${inv.tenantId}`}>{inv.tenantName}</Link>
                          ) : inv.tenantName}
                        </span>
                      ) : <span className="dl-muted">—</span>}
                      {inv.city && (
                        <span className="dl-city"><MapPin size={11} /> {inv.city}</span>
                      )}
                    </td>

                    {/* Driver / Vehicle — link to the inventory device detail */}
                    <td className="dl-td">
                      {d.driverName ? (
                        inv.deviceId ? (
                          <Link to={`/devices/${inv.deviceId}`} className="dl-driver dl-driver--link" title="View device details">
                            <User size={13} /> {d.driverName}
                          </Link>
                        ) : (
                          <span className="dl-driver"><User size={13} /> {d.driverName}</span>
                        )
                      ) : <span className="dl-muted">—</span>}
                      {/* The car, when there is one. This chip has always worn a
                          car icon while showing inv.deviceName — the PLACEMENT's
                          name, which doubled as the registration only by
                          convention, and only for the cabs somebody had named
                          that way. Since V250 the vehicle is a record of its own,
                          so the icon can finally mean what it shows. Falls back
                          to the placement name for non-cab screens, which have no
                          vehicle and never will. */}
                      {inv.vehicleRegistration ? (
                        <Link to={`/vehicles/${inv.vehicleId}`} className="dl-devname dl-devname--link" title="View vehicle">
                          <Car size={12} /> {inv.vehicleRegistration}
                        </Link>
                      ) : inv.deviceName && (
                        inv.deviceId ? (
                          <Link to={`/devices/${inv.deviceId}`} className="dl-devname dl-devname--link" title="View device details">
                            <Monitor size={12} /> {inv.deviceName}
                          </Link>
                        ) : (
                          <span className="dl-devname"><Monitor size={12} /> {inv.deviceName}</span>
                        )
                      )}
                    </td>

                    {/* Battery */}
                    <td className="dl-td">
                      {validPct(snap?.batteryPercent) != null ? (
                        <span className="dl-metric" title={`${snap.batteryPercent}%${snap.isCharging ? ' (charging)' : ' (on battery)'}`}>
                          <Battery size={14} fill="currentColor" className={`dl-metric-icon dl-metric-icon--${snap.isCharging ? 'green' : 'red'}`} />
                          <span className="dl-metric-val">{snap.batteryPercent}%</span>
                          <MiniBar pct={snap.batteryPercent} color={batteryColor(snap.batteryPercent)} />
                        </span>
                      ) : <span className="dl-muted">—</span>}
                    </td>

                    {/* Storage / Signal */}
                    <td className="dl-td">
                      <div className="dl-stack">
                        {snap?.storageFree != null ? (
                          <span className="dl-metric" title={`${fmtMB(snap.storageFree)} free of ${fmtMB(snap.storageTotal)}`}>
                            <HardDrive size={13} className={`dl-metric-icon dl-metric-icon--${storagePct > 20 ? 'blue' : 'red'}`} />
                            <span className="dl-metric-val">{fmtMB(snap.storageFree)}</span>
                            {storagePct != null && <MiniBar pct={storagePct} color={storagePct > 20 ? 'blue' : 'red'} />}
                          </span>
                        ) : <span className="dl-muted">—</span>}
                        {sig ? (
                          <span className="dl-metric" title={`${snap.signalDbm} dBm (${snap.networkType || ''})`}>
                            <Signal size={13} className={`dl-metric-icon dl-metric-icon--${sig.color}`} />
                            <Badge color={sig.color}>{sig.text}</Badge>
                          </span>
                        ) : <span className="dl-muted">—</span>}
                      </div>
                    </td>

                    {/* Uptime 24h */}
                    <td className="dl-td">
                      <span className="dl-metric" title={`${conn.disconnects} disconnects in 24h`}>
                        <Activity size={13} className={`dl-metric-icon dl-metric-icon--${conn.uptimePct >= 90 ? 'green' : conn.uptimePct >= 50 ? 'yellow' : 'red'}`} />
                        <span className="dl-metric-val">{conn.uptimePct}%</span>
                        {conn.disconnects > 0 && (
                          <span className="dl-metric-sub">{conn.disconnects}dc</span>
                        )}
                      </span>
                    </td>

                    {/* License / Inventory */}
                    <td className="dl-td">
                      <div className="dl-stack">
                        {d.license ? (
                          <Link to={`/licenses/${d.license.id}`} className="dl-lic-link" title={d.license.key}>
                            <Badge color="green">{d.license.key?.substring(5, 14)}…</Badge>
                          </Link>
                        ) : (
                          <Badge color="gray">no license</Badge>
                        )}
                        {inv.matched ? (
                          <Badge color="green">{inv.platformType?.replace('_', '-') || 'matched'}</Badge>
                        ) : (
                          <Badge color="yellow">unmatched</Badge>
                        )}
                      </div>
                    </td>

                    {/* Alerts */}
                    <td className="dl-td">
                      {d.alerts?.length > 0 ? (
                        <span className="dl-alerts" title={d.alerts.join(', ')}>
                          <AlertTriangle size={13} className="dl-metric-icon dl-metric-icon--orange" />
                          <Badge color="red">{d.alerts.length}</Badge>
                        </span>
                      ) : (
                        <Badge color="green">OK</Badge>
                      )}
                    </td>

                    {/* Last passenger detected */}
                    <td className="dl-td">
                      {d.lastPassenger ? (
                        <Link
                          to={`/ggos-devices/identity/${d.lastPassenger.unifiedId}`}
                          className="dl-pax"
                          title={`Passenger detected ${new Date(d.lastPassenger.at).toLocaleString()}`}
                        >
                          <CircleUser size={14} /> {fmtRel(d.lastPassenger.at)}
                        </Link>
                      ) : <span className="dl-muted">—</span>}
                    </td>

                    {/* Last seen + snapshot age */}
                    <td className="dl-td dl-td--right dl-td--time" title={d.lastConnectedAt ? new Date(d.lastConnectedAt).toLocaleString() : ''}>
                      {fmtRel(d.lastConnectedAt)}
                      <span className="dl-sub dl-sub--right" title={snap?.collectedAt ? `Snapshot ${new Date(snap.collectedAt).toLocaleString()}` : 'No snapshot'}>
                        {snap ? (<><Clock size={10} className="dl-metric-icon--inline" /> {fmtRel(snap.collectedAt)}</>) : 'no snapshot'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!loading && devices.length > 0 && (
        <div className="dl-pagination">
          <button className="dl-page-btn" disabled={pagination.page <= 1}
            onClick={() => handlePage(pagination.page - 1)}>
            <ChevronLeft size={16} /> Prev
          </button>
          <span className="dl-page-info">
            {pagination.page} / {pagination.totalPages}
            <span className="dl-page-total">({pagination.totalCount} devices)</span>
          </span>
          <button className="dl-page-btn" disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePage(pagination.page + 1)}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default GGOSDeviceList;
